import {
  type CV,
  type CVStorageAdapter,
  type CVSummary,
  type CreateCVRequest,
  type PDFOptions,
  type UpdateCVRequest,
  renderCVDocument,
} from '@cv-builder/core'
import { createSampleCV } from './sample-data'

const STORAGE_KEY = 'cv-builder:cvs'
const SEEDED_KEY = 'cv-builder:seeded'

export interface LocalStorageAdapterOptions {
  pdfServiceUrl?: string
  /** Whether to seed with sample data when empty (default: false) */
  seedSampleData?: boolean
}

/**
 * LocalStorage-based CV storage adapter
 * Used for demo/development without a backend
 */
export function createLocalStorageAdapter(
  options: LocalStorageAdapterOptions = {}
): CVStorageAdapter {
  const pdfServiceUrl = options.pdfServiceUrl || 'http://localhost:3100'
  const seedSampleData = options.seedSampleData ?? false

  // Helper to get all CVs from localStorage
  const getAllCVs = (): CV[] => {
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      const cvs: CV[] = data ? JSON.parse(data) : []

      // Seed sample data if empty and not already seeded
      if (cvs.length === 0 && seedSampleData && !localStorage.getItem(SEEDED_KEY)) {
        const sampleCV = createSampleCV()
        localStorage.setItem(STORAGE_KEY, JSON.stringify([sampleCV]))
        localStorage.setItem(SEEDED_KEY, 'true')
        return [sampleCV]
      }

      return cvs
    } catch {
      return []
    }
  }

  // Helper to save all CVs to localStorage
  const saveAllCVs = (cvs: CV[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cvs))
  }

  // Generate UUID
  const generateId = (): string => {
    return crypto.randomUUID()
  }

  return {
    async listCVs(): Promise<CVSummary[]> {
      const cvs = getAllCVs()
      return cvs.map((cv) => ({
        id: cv.id,
        title: cv.title,
        templateId: cv.templateId,
        updatedAt: cv.updatedAt,
      }))
    },

    async getCV(id: string): Promise<CV> {
      const cvs = getAllCVs()
      const cv = cvs.find((c) => c.id === id)
      if (!cv) {
        throw new Error(`CV not found: ${id}`)
      }
      return cv
    },

    async createCV(data?: CreateCVRequest): Promise<CV> {
      const cvs = getAllCVs()
      const id = generateId()
      const now = new Date().toISOString()

      const newCV: CV = {
        id,
        title: data?.title ?? 'Unavngivet CV',
        templateId: data?.templateId ?? 'classic',
        locale: data?.locale ?? 'da-DK',
        createdAt: now,
        updatedAt: now,
        personalInfo: {
          fullName: '',
          jobTitle: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          country: '',
          postalCode: '',
          website: '',
          linkedin: '',
          summary: '',
          photoUrl: '',
          showPhoto: false,
        },
        experiences: [],
        education: [],
        skills: [],
        languages: [],
        certifications: [],
        customSections: [],
      }

      cvs.push(newCV)
      saveAllCVs(cvs)
      return newCV
    },

    async updateCV(id: string, data: UpdateCVRequest): Promise<CV> {
      const cvs = getAllCVs()
      const index = cvs.findIndex((c) => c.id === id)

      if (index === -1) {
        throw new Error(`CV not found: ${id}`)
      }

      const existingCV = cvs[index]!
      const updatedCV: CV = {
        ...existingCV,
        ...data,
        personalInfo: data.personalInfo
          ? { ...existingCV.personalInfo, ...data.personalInfo }
          : existingCV.personalInfo,
        updatedAt: new Date().toISOString(),
      }

      cvs[index] = updatedCV
      saveAllCVs(cvs)
      return updatedCV
    },

    async deleteCV(id: string): Promise<void> {
      const cvs = getAllCVs()
      const filtered = cvs.filter((c) => c.id !== id)

      if (filtered.length === cvs.length) {
        throw new Error(`CV not found: ${id}`)
      }

      saveAllCVs(filtered)
    },

    async generatePDF(id: string, options?: PDFOptions): Promise<Blob> {
      // Get the CV data
      const cv = await this.getCV(id)

      // Render CV to HTML using the shared core renderer
      const html = renderCVDocument(cv)

      // Build margins from options or use defaults
      const margins = options?.margins ?? { top: 15, right: 20, bottom: 15, left: 20 }
      const fallbackSideMargin = margins.sides ?? 20
      const marginConfig = {
        top: `${margins.top}mm`,
        right: `${margins.right ?? fallbackSideMargin}mm`,
        bottom: `${margins.bottom}mm`,
        left: `${margins.left ?? fallbackSideMargin}mm`,
      }

      // Call pdf-service to generate PDF
      const response = await fetch(`${pdfServiceUrl}/render`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          html,
          options: {
            format: 'A4',
            printBackground: true,
            margin: marginConfig,
          },
        }),
      })

      if (!response.ok) {
        const error = await response.text().catch(() => 'Unknown error')
        throw new Error(`PDF generation failed: ${error}`)
      }

      return response.blob()
    },

    async uploadPhoto(file: File): Promise<string> {
      // Convert file to base64 data URL for localStorage
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            reject(new Error('Failed to read file'))
          }
        }
        reader.onerror = () => reject(reader.error)
        reader.readAsDataURL(file)
      })
    },
  }
}
